import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const FilterIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size || '18'} height={props.size || '18'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M3 5h18" />
        <path d="M6 12h12" />
        <path d="M10 19h4" />
    </svg>
);

const HeaderFilterDrawer = ({
    isFilterPanelOpen,
    setIsFilterPanelOpen,
    globalFilterCount,
    clearHeaderFilters,
    isDarkMode,
    drilldownPlatform,
    setDrilldownPlatform,
    selectedPlatform,
    setSelectedPlatform,
    selectedCountry,
    setSelectedCountry,
    orderStatusFilter,
    setOrderStatusFilter,
    headerPlatformOptions,
    headerCountryOptions,
    showGlobalSalesFilters,
    viewTitle,
    setCurrentPage,
    setCustomerPage,
    setCustomerVisibleLimit,
    resetCustomersRequestState
}) => {
    const triggerRef = useRef(null);
    const drawerRef = useRef(null);
    const closeRef = useRef(null);
    const muted = isDarkMode ? 'text-slate-400' : 'text-slate-500';
    const labelClass = `mb-1.5 block text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`;
    const inputClass = `h-10 w-full rounded-lg border px-3 text-sm outline-none transition-colors focus:border-[#E55C1A] focus:ring-2 focus:ring-[#E55C1A]/15 ${isDarkMode
        ? 'border-[#344457] bg-[#101b29] text-slate-100'
        : 'border-slate-300 bg-white text-slate-900'}`;

    useEffect(() => {
        if (!isFilterPanelOpen) return undefined;
        closeRef.current?.focus();
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsFilterPanelOpen(false);
                return;
            }
            if (event.key !== 'Tab' || !drawerRef.current) return;
            const focusable = Array.from(drawerRef.current.querySelectorAll(
                'button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
            ));
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            triggerRef.current?.focus();
        };
    }, [isFilterPanelOpen, setIsFilterPanelOpen]);

    const selectChannel = (value) => {
        setDrilldownPlatform(value === 'all' ? null : value);
        setSelectedPlatform(null);
        setCurrentPage(1);
    };
    const selectPlatform = (value) => {
        setSelectedPlatform(value === 'all' ? null : value);
        if (value !== 'all') setDrilldownPlatform(null);
        setCurrentPage(1);
    };
    const selectOrderStatus = (value) => {
        setOrderStatusFilter(value);
        setCurrentPage(1);
        setCustomerPage?.(1);
        setCustomerVisibleLimit?.(10);
        resetCustomersRequestState?.();
    };

    return (
        <>
            <div className="inline-flex items-center gap-1.5">
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={() => setIsFilterPanelOpen(open => !open)}
                    aria-controls="dashboard-filter-drawer"
                    aria-expanded={isFilterPanelOpen}
                    className={`inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm font-semibold transition-colors ${isFilterPanelOpen || globalFilterCount
                        ? isDarkMode ? 'border-[#E55C1A]/65 bg-[#E55C1A]/10 text-[#ff9b68]' : 'border-[#E55C1A]/50 bg-[#fff3ec] text-[#a83e0c]'
                        : isDarkMode ? 'border-[#344457] bg-[#172231] text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                >
                    <FilterIcon size={16} />
                    <span>Filter</span>
                    {globalFilterCount > 0 && <span className="rounded-full bg-[#E55C1A] px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">{globalFilterCount}</span>}
                </button>
                {globalFilterCount > 0 && (
                    <button
                        type="button"
                        onClick={clearHeaderFilters}
                        aria-label="Filter zurücksetzen"
                        title="Alle aktiven Filter zurücksetzen"
                        className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition-colors ${isDarkMode
                            ? 'border-[#6a3a27] bg-[#E55C1A]/10 text-[#ff9b68] hover:bg-[#E55C1A]/15'
                            : 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
                    >
                        <X className="h-3.5 w-3.5" strokeWidth={2.25} />
                        <span>Zurücksetzen</span>
                    </button>
                )}
            </div>
            {typeof document !== 'undefined' && createPortal(
                <div
                    className={`fixed inset-0 z-[100] font-sans ${isDarkMode ? 'dark' : ''} ${isFilterPanelOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
                    style={{
                        visibility: isFilterPanelOpen ? 'visible' : 'hidden',
                        transition: isFilterPanelOpen ? 'visibility 0s' : 'visibility 0s linear 300ms'
                    }}
                    aria-hidden={!isFilterPanelOpen}
                >
                    <button
                        type="button"
                        tabIndex={isFilterPanelOpen ? 0 : -1}
                        aria-label="Filter schließen"
                        onClick={() => setIsFilterPanelOpen(false)}
                        className={`absolute inset-0 w-full cursor-default bg-[#050b13]/45 transition-opacity duration-300 ${isFilterPanelOpen ? 'opacity-100' : 'opacity-0'}`}
                    />
                    <aside
                        id="dashboard-filter-drawer"
                        ref={drawerRef}
                        role="dialog"
                        aria-modal={isFilterPanelOpen ? 'true' : undefined}
                        aria-labelledby="dashboard-filter-title"
                        className={`absolute inset-y-0 right-0 flex w-full flex-col border-l shadow-2xl transition-transform duration-300 ease-out sm:w-[440px] ${isFilterPanelOpen ? 'translate-x-0' : 'translate-x-full'} ${isDarkMode
                            ? 'border-[#344457] bg-[#0f1824] text-slate-100 shadow-black/40'
                            : 'border-slate-200 bg-[#f8fafc] text-slate-900 shadow-slate-900/20'}`}
                    >
                        <header className={`flex items-start justify-between gap-4 border-b px-6 py-5 ${isDarkMode ? 'border-[#2b394a]' : 'border-slate-200'}`}>
                            <div>
                                <p className={`text-[11px] font-semibold uppercase tracking-[0.13em] ${muted}`}>{viewTitle || 'Ansicht'}</p>
                                <h2 id="dashboard-filter-title" className="mt-1 text-xl font-semibold tracking-tight">Filter</h2>
                                <p className={`mt-1 text-sm ${muted}`}>Ansicht und Kennzahlen eingrenzen</p>
                            </div>
                            <button
                                ref={closeRef}
                                type="button"
                                onClick={() => setIsFilterPanelOpen(false)}
                                aria-label="Filter schließen"
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors ${isDarkMode
                                    ? 'border-[#344457] text-slate-300 hover:bg-white/5'
                                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'}`}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </header>

                        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
                            {showGlobalSalesFilters && <section aria-labelledby="dashboard-sales-filter-title">
                                <div className="mb-4">
                                    <h3 id="dashboard-sales-filter-title" className="text-sm font-semibold">Vertrieb</h3>
                                    <p className={`mt-1 text-xs leading-5 ${muted}`}>Auswahl für Kennzahlen und Auswertungen dieser Seite.</p>
                                </div>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <label>
                                        <span className={labelClass}>Kanal</span>
                                        <select value={drilldownPlatform || 'all'} onChange={event => selectChannel(event.target.value)} className={inputClass}>
                                            <option value="all">Alle Kanäle</option>
                                            <option value="Shop">Shop</option>
                                            <option value="Amazon">Amazon</option>
                                            <option value="eBay">eBay</option>
                                            <option value="Other">Sonstige</option>
                                        </select>
                                    </label>
                                    <label>
                                        <span className={labelClass}>Plattform</span>
                                        <select value={selectedPlatform || 'all'} onChange={event => selectPlatform(event.target.value)} className={inputClass}>
                                            <option value="all">Alle Plattformen</option>
                                            {!headerPlatformOptions.length && <option value="" disabled>Keine Plattformdaten</option>}
                                            {headerPlatformOptions.map(name => <option key={name} value={name}>{name}</option>)}
                                        </select>
                                    </label>
                                    <label>
                                        <span className={labelClass}>Land</span>
                                        <select value={selectedCountry || 'all'} onChange={event => { setSelectedCountry(event.target.value === 'all' ? null : event.target.value); setCurrentPage(1); }} className={inputClass}>
                                            <option value="all">Alle Länder</option>
                                            {!headerCountryOptions.length && <option value="" disabled>Keine Länderdaten</option>}
                                            {headerCountryOptions.map(country => <option key={country} value={country}>{country}</option>)}
                                        </select>
                                    </label>
                                    <label>
                                        <span className={labelClass}>Auftragsstatus</span>
                                        <select value={orderStatusFilter} onChange={event => selectOrderStatus(event.target.value)} className={inputClass}>
                                            <option value="all">Alle Aufträge</option>
                                            <option value="active">Nur aktive</option>
                                            <option value="cancelled">Nur stornierte</option>
                                        </select>
                                    </label>
                                </div>
                            </section>}
                            <div id="page-filter-panel" className={`${showGlobalSalesFilters ? 'mt-7 border-t pt-6' : ''} empty:hidden ${isDarkMode ? 'border-[#2b394a]' : 'border-slate-200'}`} />
                        </div>

                        <footer className={`flex items-center justify-between gap-3 border-t px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] ${isDarkMode ? 'border-[#2b394a] bg-[#111d2b]' : 'border-slate-200 bg-white'}`}>
                            {globalFilterCount > 0 && (
                                <button
                                    type="button"
                                    onClick={clearHeaderFilters}
                                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${isDarkMode
                                        ? 'border-[#6a3a27] bg-[#E55C1A]/10 text-[#ff9b68] hover:bg-[#E55C1A]/15'
                                        : 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
                                >
                                    Zurücksetzen
                                </button>
                            )}
                            <button type="button" onClick={() => setIsFilterPanelOpen(false)} className="rounded-lg bg-[#E55C1A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#cd5016]">
                                Fertig
                            </button>
                        </footer>
                    </aside>
                </div>,
                document.body
            )}
        </>
    );
};

const HeaderFilterPanel = ({
    show = false,
    filterPanelRef,
    isFilterPanelOpen,
    setIsFilterPanelOpen,
    globalFilterCount = 0,
    clearHeaderFilters,
    showGlobalSalesFilters,
    showCustomerHeaderFilters,
    textClass,
    isDarkMode,
    filterPanelClass,
    filterSectionClass,
    filterSectionTitleClass,
    filterInputClass,
    drilldownPlatform,
    setDrilldownPlatform,
    selectedPlatform,
    setSelectedPlatform,
    selectedCountry,
    setSelectedCountry,
    orderStatusFilter = 'all',
    setOrderStatusFilter = () => { },
    headerPlatformOptions = [],
    headerCountryOptions = [],
    customerSegmentFilter,
    setCustomerSegmentFilter,
    customerSegmentFilterOptions = [],
    customerOriginFilter,
    setCustomerOriginFilter,
    customerOriginOptions = [],
    customerGroupFilter = null,
    setCustomerGroupFilter = () => { },
    customerGroupOptions = [],
    setCurrentPage,
    setCustomerPage,
    setCustomerVisibleLimit,
    resetCustomersRequestState,
    viewTitle,
    drawer = false
}) => {
    if (!show) return null;

    if (drawer) {
        return <HeaderFilterDrawer
            isFilterPanelOpen={isFilterPanelOpen}
            setIsFilterPanelOpen={setIsFilterPanelOpen}
            globalFilterCount={globalFilterCount}
            clearHeaderFilters={clearHeaderFilters}
            isDarkMode={isDarkMode}
            drilldownPlatform={drilldownPlatform}
            setDrilldownPlatform={setDrilldownPlatform}
            selectedPlatform={selectedPlatform}
            setSelectedPlatform={setSelectedPlatform}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            orderStatusFilter={orderStatusFilter}
            setOrderStatusFilter={setOrderStatusFilter}
            headerPlatformOptions={headerPlatformOptions}
            headerCountryOptions={headerCountryOptions}
            showGlobalSalesFilters={showGlobalSalesFilters}
            viewTitle={viewTitle}
            setCurrentPage={setCurrentPage}
            setCustomerPage={setCustomerPage}
            setCustomerVisibleLimit={setCustomerVisibleLimit}
            resetCustomersRequestState={resetCustomersRequestState}
        />;
    }

    const hasActiveFilters = globalFilterCount > 0;

    const panelDescription = showCustomerHeaderFilters
        ? 'Vertrieb und Kundenanalyse.'
        : showGlobalSalesFilters
            ? 'Kanal, Plattform und Land.'
            : 'Filter für diese Ansicht.';

    return (
        <div ref={filterPanelRef} className="relative flex items-center gap-2">
            <button
                type="button"
                onClick={() => setIsFilterPanelOpen(prev => !prev)}
                className={`inline-flex h-9 items-center gap-2 rounded-lg border px-2.5 text-sm font-semibold transition-all ${isFilterPanelOpen
                    ? 'border-[#E55C1A] bg-[#E55C1A] text-white shadow-sm'
                    : hasActiveFilters
                        ? isDarkMode
                            ? 'border-[#E55C1A] bg-[#E55C1A]/10 text-[#ff8a4c] shadow-[0_0_0_1px_rgba(229,92,26,0.16),0_0_16px_rgba(229,92,26,0.18)] hover:bg-[#E55C1A]/15'
                            : 'border-[#E55C1A] bg-[#fff4ed] text-[#c2410c] shadow-[0_0_0_1px_rgba(229,92,26,0.12),0_8px_22px_rgba(229,92,26,0.16)] hover:bg-[#ffeadb]'
                    : isDarkMode
                        ? 'border-[#404040] bg-[#292929] text-slate-200 hover:border-[#E55C1A]'
                        : 'border-[#d8e0ea] bg-white text-[#334155] hover:border-[#c2410c] shadow-sm'
                }`}
                title="Globale Filter"
            >
                <FilterIcon size={16} />
                <span>Filter</span>
                {hasActiveFilters && (
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-black ${isFilterPanelOpen ? 'bg-white/20 text-white' : 'bg-[#E55C1A] text-white'}`}>
                        {globalFilterCount}
                    </span>
                )}
            </button>

            {hasActiveFilters && (
                <button
                    type="button"
                    onClick={clearHeaderFilters}
                    aria-label="Alle aktiven Filter zurücksetzen"
                    title="Alle aktiven Filter zurücksetzen"
                    className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition-colors ${isDarkMode
                        ? 'border-[#6a3a27] bg-[#E55C1A]/10 text-[#ff9b68] hover:bg-[#E55C1A]/15'
                        : 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
                >
                    <X className="h-3.5 w-3.5" strokeWidth={2.25} />
                    <span className="hidden xl:inline">Zurücksetzen</span>
                </button>
            )}


            <div
                className={`absolute right-0 top-[calc(100%+10px)] z-50 max-h-[calc(100vh-120px)] w-[390px] max-w-[calc(100vw-32px)] overflow-y-auto rounded-2xl border p-4 shadow-2xl transition-all duration-150 ${
                    isFilterPanelOpen
                        ? 'visible translate-y-0 opacity-100'
                        : 'invisible pointer-events-none -translate-y-1 opacity-0'
                } ${filterPanelClass}`}
                onMouseDown={(event) => event.stopPropagation()}
                aria-hidden={!isFilterPanelOpen}
            >
                <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                        <p className={`text-sm font-black ${textClass}`}>Filter</p>
                        <p className={`mt-0.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {panelDescription}
                        </p>
                    </div>
                    {globalFilterCount > 0 && (
                        <button
                            onClick={clearHeaderFilters}
                            className={`rounded-md border px-2.5 py-1.5 text-xs font-bold transition-colors ${isDarkMode ? 'border-[#6a3a27] bg-[#E55C1A]/10 text-[#ff9b68] hover:bg-[#E55C1A]/15' : 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100'}`}
                        >
                            Zurücksetzen
                        </button>
                    )}
                </div>

                <div className="space-y-3">
                    {showGlobalSalesFilters && (
                        <section className={`border-t pt-3 ${filterSectionClass}`}>
                            <p className={filterSectionTitleClass}>Vertrieb</p>

                            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <label className="block">
                                    <span className={filterSectionTitleClass}>Kanal</span>
                                    <select
                                        value={drilldownPlatform || 'all'}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setDrilldownPlatform(value === 'all' ? null : value);
                                            setSelectedPlatform(null);
                                            setCurrentPage(1);
                                        }}
                                        className={`mt-1.5 h-10 w-full rounded-xl border px-3 text-sm font-semibold outline-none ${filterInputClass}`}
                                    >
                                        <option value="all">Alle Kanäle</option>
                                        <option value="Shop">Shop</option>
                                        <option value="Amazon">Amazon</option>
                                        <option value="eBay">eBay</option>
                                        <option value="Other">Sonstige</option>
                                    </select>
                                </label>

                                <label className="block">
                                    <span className={filterSectionTitleClass}>Plattform</span>
                                    <select
                                        value={selectedPlatform || 'all'}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setSelectedPlatform(value === 'all' ? null : value);
                                            if (value !== 'all') setDrilldownPlatform(null);
                                            setCurrentPage(1);
                                        }}
                                        className={`mt-1.5 h-10 w-full rounded-xl border px-3 text-sm font-semibold outline-none ${filterInputClass}`}
                                    >
                                        <option value="all">Alle Plattformen</option>
                                        {!headerPlatformOptions.length && (
                                            <option value="" disabled>Keine Plattformdaten im Zeitraum</option>
                                        )}
                                        {headerPlatformOptions.map(platformName => (
                                            <option key={platformName} value={platformName}>{platformName}</option>
                                        ))}
                                    </select>
                                </label>

                                <label className="block">
                                    <span className={filterSectionTitleClass}>Land</span>
                                    <select
                                        value={selectedCountry || 'all'}
                                        onChange={(event) => {
                                            setSelectedCountry(event.target.value === 'all' ? null : event.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className={`mt-1.5 h-10 w-full rounded-xl border px-3 text-sm font-semibold outline-none ${filterInputClass}`}
                                    >
                                        <option value="all">Alle Länder</option>
                                        {!headerCountryOptions.length && (
                                            <option value="" disabled>Keine Länderdaten im Zeitraum</option>
                                        )}
                                        {headerCountryOptions.map(country => (
                                            <option key={country} value={country}>{country}</option>
                                        ))}
                                    </select>
                                </label>

                                <label className="block">
                                    <span className={filterSectionTitleClass}>Auftragsstatus</span>
                                    <select
                                        value={orderStatusFilter}
                                        onChange={(event) => {
                                            setOrderStatusFilter(event.target.value);
                                            setCurrentPage(1);
                                            setCustomerPage?.(1);
                                            setCustomerVisibleLimit?.(10);
                                            resetCustomersRequestState?.();
                                        }}
                                        className={`mt-1.5 h-10 w-full rounded-xl border px-3 text-sm font-semibold outline-none ${filterInputClass}`}
                                    >
                                        <option value="all">Alle Aufträge</option>
                                        <option value="active">Nur aktive</option>
                                        <option value="cancelled">Nur stornierte</option>
                                    </select>
                                </label>
                            </div>
                        </section>
                    )}

                    {showCustomerHeaderFilters && (
                        <section className={`border-t pt-3 ${filterSectionClass}`}>
                            <p className={filterSectionTitleClass}>Kunden</p>

                            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <label className="block">
                                    <span className={filterSectionTitleClass}>Segment</span>
                                    <select
                                        value={customerSegmentFilter || 'all'}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setCustomerSegmentFilter(value === 'all' ? null : value);
                                            setCustomerPage(1);
                                            setCustomerVisibleLimit(10);
                                            resetCustomersRequestState();
                                        }}
                                        className={`mt-1.5 h-10 w-full rounded-xl border px-3 text-sm font-semibold outline-none ${filterInputClass}`}
                                    >
                                        {customerSegmentFilterOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label} · {option.hint}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <label className="block">
                                    <span className={filterSectionTitleClass}>Herkunft</span>
                                    <select
                                        value={customerOriginFilter || 'all'}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setCustomerOriginFilter(value === 'all' ? null : value);
                                            setCustomerPage(1);
                                            setCustomerVisibleLimit(10);
                                            resetCustomersRequestState();
                                        }}
                                        className={`mt-1.5 h-10 w-full rounded-xl border px-3 text-sm font-semibold outline-none ${filterInputClass}`}
                                    >
                                        <option value="all">Alle Herkünfte</option>
                                        {customerOriginOptions.map(origin => (
                                            <option key={origin} value={origin}>{origin}</option>
                                        ))}
                                    </select>
                                </label>

                                <label className="block sm:col-span-2">
                                    <span className={filterSectionTitleClass}>Kundengruppe</span>
                                    <select
                                        value={customerGroupFilter || 'all'}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setCustomerGroupFilter(value === 'all' ? null : value);
                                            setCustomerPage(1);
                                            setCustomerVisibleLimit(10);
                                            resetCustomersRequestState();
                                        }}
                                        className={`mt-1.5 h-10 w-full rounded-xl border px-3 text-sm font-semibold outline-none ${filterInputClass}`}
                                    >
                                        <option value="all">Alle Kundengruppen</option>
                                        {customerGroupOptions.map(group => (
                                            <option key={group} value={group}>{group}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </section>
                    )}

                    <div id="page-filter-panel" className={`border-t pt-3 empty:hidden ${filterSectionClass}`} />
                </div>
            </div>
        </div>
    );
};

export const ActiveFiltersContext = React.createContext({ filters: [], isDarkMode: false });

export const VisualActiveFilters = ({ className = '', excludeKeys = [], excludeLabels = [] }) => {
    const context = React.useContext(ActiveFiltersContext);
    const filters = context.filters.filter(filter => !excludeKeys.includes(filter.key) && !excludeLabels.includes(filter.label));
    const { isDarkMode = false } = context;
    if (!filters.length) return null;

    const visibleFilters = filters.slice(0, 4);
    const hiddenCount = filters.length - visibleFilters.length;

    return (
        <div className={`flex min-w-0 flex-wrap items-center gap-1.5 ${className}`} aria-label="Aktive Filter">
            <span className="mr-0.5 shrink-0 text-[10px] font-bold uppercase tracking-wide text-slate-500">Aktiv</span>
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                {visibleFilters.map((filter) => (
                    <span
                        key={filter.key}
                        className={`inline-flex max-w-[min(280px,100%)] items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold ${isDarkMode ? 'border-[#3d4a59] bg-[#15202d] text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-700'}`}
                        title={filter.label}
                    >
                        <span className="truncate">{filter.label}</span>
                    </span>
                ))}
                {hiddenCount > 0 && (
                    <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${isDarkMode ? 'bg-[#15202d] text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                        +{hiddenCount}
                    </span>
                )}
            </div>
        </div>
    );
};
export default HeaderFilterPanel;
